/**
 * Bulk-selection model for a server-paged table.
 *
 * Two modes:
 *   - `rows`: an explicit set of rows (possibly spanning pages). Rows are kept,
 *     not just keys, because a bulk action needs each row's username / id and
 *     rows from other pages are no longer in memory.
 *   - `all`: every row matching the current filter, including ones never
 *     loaded. Sent to the server as `all: true` + the filter.
 *
 * A selection belongs to the filter it was made under (`filterKey`). Changing
 * a filter empties it: the old bug was "select all" quietly carrying over to
 * a different filter, so the next bulk action hit rows nobody was looking at.
 */
export interface SelectionState<R> {
  mode: 'rows' | 'all';
  rows: ReadonlyMap<string, R>;
  filterKey: string;
}

export function emptySelection<R>(filterKey: string): SelectionState<R> {
  return { mode: 'rows', rows: new Map(), filterKey };
}

/** The selection as it applies to `filterKey`: empty if it was made under another filter. */
export function forFilter<R>(sel: SelectionState<R>, filterKey: string): SelectionState<R> {
  return sel.filterKey === filterKey ? sel : emptySelection(filterKey);
}

export function isRowSelected<R>(sel: SelectionState<R>, key: string): boolean {
  return sel.mode === 'all' || sel.rows.has(key);
}

export function selectedCount<R>(sel: SelectionState<R>, total: number): number {
  return sel.mode === 'all' ? total : sel.rows.size;
}

export function hasSelection<R>(sel: SelectionState<R>, total: number): boolean {
  return selectedCount(sel, total) > 0;
}

/**
 * Toggle one row. In `all` mode there's no way to express "all except one" to
 * the server, so unticking a row narrows the selection to the visible page
 * minus that row — what you can see is exactly what stays selected.
 */
export function toggleRow<R>(
  sel: SelectionState<R>,
  key: string,
  row: R,
  page: readonly { key: string; row: R }[],
): SelectionState<R> {
  if (sel.mode === 'all') {
    const rows = new Map<string, R>();
    for (const p of page) if (p.key !== key) rows.set(p.key, p.row);
    return { mode: 'rows', rows, filterKey: sel.filterKey };
  }
  const rows = new Map(sel.rows);
  if (rows.has(key)) rows.delete(key);
  else rows.set(key, row);
  return { ...sel, rows };
}

/**
 * Set several rows of the visible page to `checked` (a shift-click range).
 * Same rule as `toggleRow` in `all` mode: unticking narrows the selection to
 * the visible page minus those rows; ticking leaves "all" as it is.
 */
export function setRows<R>(
  sel: SelectionState<R>,
  keys: readonly string[],
  checked: boolean,
  page: readonly { key: string; row: R }[],
): SelectionState<R> {
  if (sel.mode === 'all') {
    if (checked) return sel;
    const drop = new Set(keys);
    const rows = new Map<string, R>();
    for (const p of page) if (!drop.has(p.key)) rows.set(p.key, p.row);
    return { mode: 'rows', rows, filterKey: sel.filterKey };
  }
  const onPage = new Map(page.map((p) => [p.key, p.row]));
  const rows = new Map(sel.rows);
  for (const key of keys) {
    const row = onPage.get(key);
    if (!checked) rows.delete(key);
    else if (row !== undefined) rows.set(key, row);
  }
  return { ...sel, rows };
}

export type PageCheckState = 'none' | 'some' | 'all';

export function pageCheckState<R>(sel: SelectionState<R>, pageKeys: readonly string[]): PageCheckState {
  if (pageKeys.length === 0) return 'none';
  if (sel.mode === 'all') return 'all';
  const n = pageKeys.filter((k) => sel.rows.has(k)).length;
  return n === 0 ? 'none' : n === pageKeys.length ? 'all' : 'some';
}

/** Header checkbox: select the whole page, or clear it if it's already fully selected. */
export function togglePage<R>(sel: SelectionState<R>, page: readonly { key: string; row: R }[]): SelectionState<R> {
  if (sel.mode === 'all') return emptySelection(sel.filterKey);
  const rows = new Map(sel.rows);
  const allOn = page.length > 0 && page.every((p) => rows.has(p.key));
  for (const p of page) {
    if (allOn) rows.delete(p.key);
    else rows.set(p.key, p.row);
  }
  return { ...sel, rows };
}

export function selectAllMatching<R>(sel: SelectionState<R>): SelectionState<R> {
  return { mode: 'all', rows: new Map(), filterKey: sel.filterKey };
}

/** Offer "Select all N matching" once the visible page is fully ticked and more exist. */
export function canSelectAllMatching<R>(
  sel: SelectionState<R>,
  pageKeys: readonly string[],
  total: number,
): boolean {
  return sel.mode === 'rows' && pageCheckState(sel, pageKeys) === 'all' && total > sel.rows.size;
}

/** What a bulk action should send: explicit rows, or "everything matching this filter". */
export type SelectionTarget<R, F> = { kind: 'rows'; rows: R[] } | { kind: 'all'; filter: F; count: number };

export function selectionTarget<R, F>(sel: SelectionState<R>, filter: F, total: number): SelectionTarget<R, F> {
  return sel.mode === 'all' ? { kind: 'all', filter, count: total } : { kind: 'rows', rows: [...sel.rows.values()] };
}

/** Stable key for a filter object: drops empty values and sorts keys. */
export function filterKeyOf(filter: object): string {
  const entries = Object.entries(filter)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'any')
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}
